import React, { useRef, useEffect, useState } from 'react';
import { formatTime } from '../utils/timeUtils';

const VideoPlayer = ({ videoSrc, cloudflareStream, streamSrc, onTimeUpdate, fallbackMessage, onRef, isCustomFullScreen }) => {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  
  // Expose methods to control video playback
  useEffect(() => {
    if (onRef) {
      const controls = {
        play: () => {
          if (cloudflareStream) {
            // Send play message to iframe
            iframeRef.current?.contentWindow.postMessage(JSON.stringify({ action: 'play' }), '*');
          } else if (videoRef.current) {
            videoRef.current.play();
          }
        },
        pause: () => {
          if (cloudflareStream) {
            // Send pause message to iframe
            iframeRef.current?.contentWindow.postMessage(JSON.stringify({ action: 'pause' }), '*');
          } else if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      };
      
      onRef(controls);
    }
  }, [cloudflareStream, onRef]);
  
  // For regular HTML5 video
  useEffect(() => {
    if (cloudflareStream) return; // Skip for Cloudflare Stream
    
    const videoElement = videoRef.current;
    
    // Function to handle time updates
    const handleTimeUpdate = () => {
      if (videoElement) {
        const time = videoElement.currentTime;
        setCurrentTime(time);
        onTimeUpdate(time);
      }
    };
    
    // Function to handle video errors
    const handleError = () => {
      setVideoError(true);
      // Still provide a time update of 0 to ensure overlays work
      setCurrentTime(0);
      onTimeUpdate(0);
    };
    
    // Add event listeners
    if (videoElement) {
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      videoElement.addEventListener('error', handleError);
    }
    
    // Clean up event listeners on component unmount
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('error', handleError);
      }
    };
  }, [onTimeUpdate, cloudflareStream]);
  
  // For Cloudflare Stream iframe
  useEffect(() => {
    if (!cloudflareStream) return; // Skip for regular video
    
    // Function to handle messages from the iframe
    const handleMessage = (event) => {
      // Check if the message is from our iframe
      if (event.source === iframeRef.current?.contentWindow) {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'timeupdate') {
            setCurrentTime(data.time);
            onTimeUpdate(data.time);
          } else if (data.event === 'error') {
            console.error('Cloudflare Stream error:', data.error);
            setVideoError(true);
          }
        } catch (e) {
          // Ignore non-JSON messages
          console.log('Received non-JSON message from iframe:', event.data);
        }
      }
    };
    
    // Add event listener for messages
    window.addEventListener('message', handleMessage);
    
    // Clean up
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [cloudflareStream, onTimeUpdate]);
  
  // Effect to disable native fullscreen button
  useEffect(() => {
    if (!cloudflareStream || !iframeRef.current) return;
    
    // Function to send message to disable fullscreen button
    const disableFullscreenButton = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ 
            action: 'setFullscreenButton', 
            enabled: false // Always disable the native fullscreen button
          }), 
          '*'
        );
      }
    };
    
    // Send message immediately
    disableFullscreenButton();
    
    // Also send message after a short delay to ensure iframe is loaded
    const timeoutId = setTimeout(disableFullscreenButton, 1000);
    
    // Clean up timeout
    return () => clearTimeout(timeoutId);
  }, [cloudflareStream]);
  
  if (videoError && fallbackMessage) {
    return (
      <div className="relative bg-black aspect-video flex items-center justify-center">
        {fallbackMessage}
      </div>
    );
  }
  
  if (cloudflareStream) {
    return (
      <div className={`relative ${isCustomFullScreen ? 'h-full' : 'aspect-video'}`}>
        {videoError && fallbackMessage ? (
          <div className="bg-black aspect-video flex items-center justify-center">
            {fallbackMessage}
          </div>
        ) : (
          <>
          <iframe
            ref={iframeRef}
            src={streamSrc}
            className={`w-full ${isCustomFullScreen ? 'h-full' : ''}`}
            style={{ 
              border: 'none',
              aspectRatio: isCustomFullScreen ? 'auto' : '16/9'
            }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen={false} // Never allow native fullscreen
            loading="lazy"
            title="Lady of the Camellias - Purple pas de deux"
            onError={() => setVideoError(true)}
          ></iframe>
            
            {/* Time display */}
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm font-mono">
              {formatTime(currentTime)}
            </div>
          </>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative">
      <video 
        ref={videoRef}
        className="w-full"
        controls
        preload="metadata"
        onError={() => setVideoError(true)}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Time display */}
      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm font-mono">
        {formatTime(currentTime)}
      </div>
    </div>
  );
};

export default VideoPlayer;
